import { MdsText, MdsTextSize, MdsTextWeight } from "@/design-system/components/text";
import {
  FeaturedApp,
  FooterBackButton,
  FooterButtons,
  FooterContainer,
  FooterNextButton,
  SkipLink,
  StepTitle,
  WizardContentWrapper,
} from "@/components/onboarding/components/ImportWizard/StyledComponents";
import AppStoreBadge from "@/assets/images/Download_on_the_Mac_App_Store_Badge_US-UK_RGB_blk_092917.svg";
import { StepProps } from "@/components/onboarding/components/ImportWizard";
import { DropTargetStep } from "@/components/onboarding/components/ImportWizard/steps/DropTargetStep";
import type { StepMap } from "@/components/onboarding/components/ImportWizard/ImportWizard.types";
import { mdsBorderRadius, mdsColors } from "@/design-system/foundations";
import { css } from "@/domains/emotion";

const StepTitleCopy =
  "Bring in your Apple Notes so you can find them with Mem Search, Chat, and Copilot";

const AppleNotesStep1 = ({ onNavToStep, onSkip }: StepProps) => (
  <WizardContentWrapper>
    <StepTitle>{StepTitleCopy}</StepTitle>

    <MdsText size={MdsTextSize.Small} weight={MdsTextWeight.SemiBold}>
      Step 1. Download the Apple Notes Exporter app
    </MdsText>
    <MdsText size={MdsTextSize.Small}>
      You&apos;ll use this in the next step to export your Apple Notes
    </MdsText>

    <FeaturedApp>
      <img src="/onboarding/exporter-logo.png" width="94" height="94" />
      <MdsText
        size={MdsTextSize.Large}
        weight={MdsTextWeight.SemiBold}
        color={mdsColors().grey.x600}
        className={css({ marginTop: 8 })}
      >
        Exporter
      </MdsText>
      <MdsText
        size={MdsTextSize.Small}
        color={mdsColors().grey.x600}
        className={css({ marginBottom: 15 })}
      >
        Export Apple Notes
      </MdsText>
      <a
        href="https://apps.apple.com/us/app/exporter/id1099120373?mt=12"
        target="_blank"
        rel="noreferrer"
        className={css({ marginBottom: -5 })}
      >
        <img alt="Download on the Mac App Store" src={AppStoreBadge} width="156" height="40" />
      </a>
    </FeaturedApp>

    <AppleNotesFooter onNext={() => onNavToStep?.("apple-notes-2")} onSkip={onSkip} step={1} />
  </WizardContentWrapper>
);

const AppleNotesStep2 = ({ onNavToStep, onSkip }: StepProps) => (
  <WizardContentWrapper>
    <StepTitle>{StepTitleCopy} </StepTitle>

    <MdsText size={MdsTextSize.Small} weight={MdsTextWeight.SemiBold}>
      Step 2. Export your Apple Notes
    </MdsText>
    <MdsText size={MdsTextSize.Small}>
      Use the Exporter app downloaded in the previous step to export all your notes to a new folder
      on your computer
    </MdsText>

    <img
      src="/onboarding/exporter-usage.gif"
      width="370"
      height="229"
      className={css({
        display: "flex",
        alignSelf: "center",
        boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.2), 0px 15px 30px 3px rgba(0, 0, 0, 0.2)",
        borderRadius: mdsBorderRadius().mediumLarge,
        zIndex: 10,
      })}
    />

    <AppleNotesFooter
      onBack={() => onNavToStep?.("apple-notes-1")}
      onNext={() => onNavToStep?.("apple-notes-3")}
      onSkip={onSkip}
      step={2}
    />
  </WizardContentWrapper>
);

const AppleNotesStep3: React.FC<StepProps> = ({ onNavToStep, onClose, onSkip }) => {
  return (
    <WizardContentWrapper>
      <StepTitle>{StepTitleCopy}</StepTitle>

      <MdsText size={MdsTextSize.Small} weight={MdsTextWeight.SemiBold}>
        Step 3. Import your Apple Notes into Mem
      </MdsText>
      <MdsText size={MdsTextSize.Small}>
        Import your notes by dragging and dropping the folder of notes you exported in the previous
        step into the area below
      </MdsText>

      <DropTargetStep
        onDrop={() => {
          onClose?.();
        }}
      />

      <AppleNotesFooter onBack={() => onNavToStep?.("apple-notes-2")} step={3} onSkip={onSkip} />
    </WizardContentWrapper>
  );
};

interface AppleNotesFooterProps {
  onBack?: () => void;
  onNext?: () => void;
  onSkip?: () => void;
  step: number;
}

const AppleNotesFooter = ({ onBack, onNext, onSkip, step }: AppleNotesFooterProps) => (
  <FooterContainer>
    <FooterButtons>
      <FooterBackButton onClick={onBack} isDisabled={!onBack} />
      <div>Step {step}/3</div>
      <FooterNextButton onClick={onNext} isDisabled={!onNext} />
    </FooterButtons>
    <div>
      <SkipLink onClick={onSkip}>Skip for now</SkipLink>
    </div>
  </FooterContainer>
);

export const AppleNotesStepsMap: StepMap = {
  "apple-notes-1": {
    render: ({ onNavToStep, onSkip }) => (
      <AppleNotesStep1 onNavToStep={onNavToStep} onSkip={onSkip} />
    ),
  },
  "apple-notes-2": {
    render: ({ onNavToStep, onSkip }) => (
      <AppleNotesStep2 onNavToStep={onNavToStep} onSkip={onSkip} />
    ),
  },
  "apple-notes-3": {
    render: ({ onNavToStep, onClose, onSkip }) => (
      <AppleNotesStep3 onNavToStep={onNavToStep} onClose={onClose} onSkip={onSkip} />
    ),
  },
};
